import { get } from "svelte/store"
import {
  ControlClientError,
  ControlWebClient,
  type ControlClientErrorOptions,
  type MountedControlWebClient,
  type RequestOptions,
  type ControlWebClientOptions,
} from "@kontextwerk/control-web-client"
import {
  captureControlContext,
  controlContext,
  rejectControlContext,
} from "@kontextwerk/control-web-frame/shared/logic/authentication"
import { apiBaseOverride } from "../../../_shared/_logic/store"
import { baseAPIURL } from "../../_shared/_logic/config"

const DEFAULT_PREFIX = "/api/v1"

type BaseConfig = {
  baseUrl: string
  apiPrefix: string
}

const splitBaseUrl = (raw: string): BaseConfig => {
  try {
    const parsed = new URL(raw)
    const apiPrefix = parsed.pathname.replace(/\/+$/, "") || DEFAULT_PREFIX

    return { baseUrl: parsed.origin, apiPrefix }
  } catch {
    const normalized = raw.replace(/\/+$/, "")
    const match = normalized.match(/^(https?:\/\/[^/]+)(\/.*)$/)

    if (match) {
      const baseUrl = match[1]
      const apiPrefix = match[2] || DEFAULT_PREFIX

      return { baseUrl, apiPrefix: apiPrefix === "/" ? DEFAULT_PREFIX : apiPrefix }
    }

    return { baseUrl: normalized || "http://localhost:8000", apiPrefix: DEFAULT_PREFIX }
  }
}

const normalizeAdminPath = (path: string): string => {
  if (path.startsWith("/admin")) return path

  if (!path.startsWith("/")) return `/admin/${path}`

  return `/admin${path}`
}

type KehrblattRawApi = {
  request<T>(method: string, path: string, options?: RequestOptions): Promise<T>
  requestWithMeta<T>(
    method: string,
    path: string,
    options?: RequestOptions,
  ): Promise<{ data: T; headers: Headers; status: number }>
}

type KehrblattAdminApi = {
  raw: KehrblattRawApi
}

type KehrblattMount = {
  kehrblatt: {
    admin: KehrblattAdminApi
  }
}

type KehrblattControlWebClient = MountedControlWebClient<KehrblattMount>

const ORG_QUERY_PREFIXES = [
  "/admin/analytics/",
  "/admin/customer-registry/",
  "/admin/imports/customer-drafts/",
  "/admin/inbox-cases/",
  "/admin/property-customer-registry/",
  "/admin/property-registry/",
  "/admin/tutorials/",
  "/admin/unit-registry/",
]

const ORG_QUERY_ROOT_OR_SUBPATHS = [
  "/admin/appointment-contact-plans",
  "/admin/feedback",
  "/admin/notifications",
]

const ORG_QUERY_GET_EXACT_PATHS = new Set([
  "/admin/audit/events",
  "/admin/case-communications",
  "/admin/district/statistics",
  "/admin/letters/work-orders/batch/pdf",
  "/admin/order-types",
  "/admin/permissions/catalog",
  "/admin/schedule-events",
  "/admin/twilio/access-token",
  "/admin/twilio/capability-token",
])

const ORG_QUERY_POST_EXACT_PATHS = new Set([
  "/admin/contact-orders/batch/cancel",
  "/admin/contact-orders/batch/complete",
  "/admin/letters/work-orders/delivery-estimate",
])

const WORK_ORDERS_NON_QUERY_PATHS = new Set([
  "/admin/work-orders/restore-batch",
  "/admin/work-orders/tour-sheet/pdf",
])

const acceptsLettersOrganizationQueryParam = (method: string, pathname: string): boolean => {
  if (method === "GET") {
    return (
      /^\/admin\/letters\/work-orders\/print-jobs\/(?!manual-delivery$)[^/]+$/.test(pathname) ||
      /^\/admin\/letters\/work-orders\/print-jobs\/[^/]+\/(?:download|manual-delivery-download)$/.test(
        pathname,
      ) ||
      /^\/admin\/letters\/work-orders\/(?!print-jobs$|batch$)[^/]+(?:\/pdf)?$/.test(pathname)
    )
  }

  if (method === "POST") {
    return /^\/admin\/letters\/work-orders\/print-jobs\/[^/]+\/(?:delivery-estimate|manual-delivery-download)$/.test(
      pathname,
    )
  }

  return false
}

const acceptsOrganizationQueryParam = (method: string, rawPathname: string): boolean => {
  const upperMethod = method.toUpperCase()
  const pathname = normalizeAdminPath(rawPathname).replace(/\/+$/, "") || "/admin"

  if (ORG_QUERY_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return true

  if (
    ORG_QUERY_ROOT_OR_SUBPATHS.some(
      (basePrefix) => pathname === basePrefix || pathname.startsWith(`${basePrefix}/`),
    )
  ) {
    return true
  }

  if (
    (pathname === "/admin/work-orders" || pathname.startsWith("/admin/work-orders/")) &&
    !WORK_ORDERS_NON_QUERY_PATHS.has(pathname)
  ) {
    return true
  }

  if (upperMethod === "GET" && pathname.startsWith("/admin/activity/")) return true

  if (upperMethod === "GET" && ORG_QUERY_GET_EXACT_PATHS.has(pathname)) return true

  if (upperMethod === "POST" && ORG_QUERY_POST_EXACT_PATHS.has(pathname)) return true

  return acceptsLettersOrganizationQueryParam(upperMethod, pathname)
}

/**
 * KB product data belongs to exactly one organization: query-scoped endpoints receive the
 * confirmed context organization, while routes that bind tenant identity via path, body, form,
 * or session contract remain untouched so strict server query validation is respected.
 */
export const withContextOrganization = (
  path: string,
  organizationId: string | null,
  method = "GET",
): string => {
  if (!organizationId) return path

  const hashIndex = path.indexOf("#")
  const target = hashIndex === -1 ? path : path.slice(0, hashIndex)
  const hash = hashIndex === -1 ? "" : path.slice(hashIndex)
  const queryIndex = target.indexOf("?")
  const pathname = queryIndex === -1 ? target : target.slice(0, queryIndex)
  const query = new URLSearchParams(queryIndex === -1 ? "" : target.slice(queryIndex + 1))

  if (query.has("organization_id") || !acceptsOrganizationQueryParam(method, pathname)) {
    return path
  }

  query.set("organization_id", organizationId)

  return `${pathname}?${query.toString()}${hash}`
}

const scopedAdminPath = (path: string, method: string) =>
  withContextOrganization(
    normalizeAdminPath(path),
    get(controlContext)?.organizationId ?? null,
    method,
  )

const createKehrblattRawApi = (client: ControlWebClient): KehrblattRawApi => ({
  request<T>(method: string, path: string, options: RequestOptions = {}) {
    return client.transport.request<T>(method, scopedAdminPath(path, method), {
      ...options,
      withAuth: options.withAuth ?? true,
    })
  },
  requestWithMeta<T>(method: string, path: string, options: RequestOptions = {}) {
    return client.transport.requestWithMeta<T>(method, scopedAdminPath(path, method), {
      ...options,
      withAuth: options.withAuth ?? true,
    })
  },
})

let cachedClient: KehrblattControlWebClient | null = null
let cachedBase = ""
let cachedPrefix = ""

const resolveBase = (): BaseConfig => {
  const override = get(apiBaseOverride)
  const rawBase = (override && override.trim()) || baseAPIURL

  return splitBaseUrl(rawBase)
}

const statusOf = (error: unknown): number | undefined =>
  (error as { statusCode?: number; status?: number } | null)?.statusCode ??
  (error as { status?: number } | null)?.status

/**
 * KB product transport, bound like the frame transport: every authenticated request carries the
 * confirmed V2 context captured at request start, writes are never replayed, and a request without
 * a confirmed context never leaves the tab. A 401 tells the root to drop protected state.
 */
export const getControlWebClient = (
  options?: Partial<ControlWebClientOptions>,
): KehrblattControlWebClient => {
  const { baseUrl, apiPrefix } = resolveBase()

  if (!cachedClient || cachedBase !== baseUrl || cachedPrefix !== apiPrefix) {
    cachedClient = new ControlWebClient({
      ...options,
      baseUrl,
      apiPrefix,
      timeoutMs: 120_000,
      retry: {
        maxAttempts: 1,
        backoffMs: 0,
        retryStatusCodes: [],
      },
      contextBinding: "required",
      captureContext: captureControlContext,
      onError: (error) => {
        if (statusOf(error) === 401) rejectControlContext()
      },
    }).mount("kehrblatt", ({ client }) => ({
      admin: {
        raw: createKehrblattRawApi(client),
      },
    }))
    cachedBase = baseUrl
    cachedPrefix = apiPrefix
  }

  return cachedClient
}

export const getAdminApi = () => getControlWebClient().admin
export const getPublicApi = () => getControlWebClient().public
export const getKehrblattAdminApi = () => getControlWebClient().kehrblatt.admin

export const buildQueryString = (
  params: Record<
    string,
    string | number | boolean | Array<string | number | boolean> | undefined | null
  >,
) => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null && entry !== "") {
          search.append(key, String(entry))
        }
      })
      continue
    }

    search.set(key, String(value))
  }
  const qs = search.toString()

  return qs ? `?${qs}` : ""
}

export const serializeCsvQuery = (
  values?: Array<string | number | boolean | null | undefined> | null,
): string | undefined => {
  if (!values?.length) return undefined

  const filtered = values
    .map((value) => (value === undefined || value === null ? "" : String(value).trim()))
    .filter(Boolean)

  return filtered.length ? filtered.join(",") : undefined
}

export class ApiError extends ControlClientError {
  constructor(
    status: number,
    message: string,
    payload?: unknown,
    options?: ControlClientErrorOptions,
  ) {
    super(message, {
      statusCode: status,
      detail: payload ?? options?.detail,
      responseText: options?.responseText,
      reasonCode: options?.reasonCode,
      requestId: options?.requestId,
      fieldErrors: options?.fieldErrors,
    })
    this.status = status
    this.payload = payload ?? options?.detail
  }
}

const inflightReads = new Map<string, Promise<unknown>>()

const readKey = (method: string, path: string, init: RequestInit) => {
  // A caller-owned signal also owns that request's lifetime. Sharing the
  // promise would let one unmount abort an otherwise independent consumer.
  if (method !== "GET" || init.body || init.signal) return null

  const headers = new Headers(init.headers ?? undefined)
  const session = get(controlContext)?.session ?? null

  return JSON.stringify({
    base: resolveBase(),
    // Reads of different confirmed contexts never share a response.
    context: session
      ? [session.product_issuer, session.session_id, session.context_revision]
      : null,
    method,
    path: scopedAdminPath(path, method),
    headers: Array.from(headers.entries()).sort(([left], [right]) => left.localeCompare(right)),
  })
}

const dedupeRead = async <T>(key: string | null, loader: () => Promise<T>): Promise<T> => {
  if (!key) return loader()

  const existing = inflightReads.get(key)

  if (existing) return existing as Promise<T>

  const next = loader().finally(() => {
    inflightReads.delete(key)
  })
  inflightReads.set(key, next)

  return next
}

const requestHeaders = (init: RequestInit): Headers => {
  const headers = new Headers(init.headers)

  if (typeof init.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json")
  }

  return headers
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase()
  const headers = requestHeaders(init)
  const body = init.body ?? undefined

  try {
    return await dedupeRead(readKey(method, path, init), () =>
      getKehrblattAdminApi().raw.request<T>(method, path, {
        body,
        headers: Object.fromEntries(headers.entries()),
        signal: init.signal,
      }),
    )
  } catch (err) {
    if (err instanceof ControlClientError) {
      throw new ApiError(err.statusCode ?? err.status ?? 0, err.message, err.detail, {
        statusCode: err.statusCode ?? err.status,
        detail: err.detail,
        responseText: err.responseText,
        reasonCode: err.reasonCode,
        requestId: err.requestId,
        fieldErrors: err.fieldErrors,
      })
    }

    throw err
  }
}

export async function requestWithHeaders<T>(
  path: string,
  init: RequestInit = {},
): Promise<{ data: T; headers: Headers; status: number }> {
  const method = (init.method ?? "GET").toUpperCase()
  const headers = requestHeaders(init)

  return dedupeRead(readKey(method, path, init), () =>
    getKehrblattAdminApi().raw.requestWithMeta<T>(method, path, {
      body: init.body ?? undefined,
      headers: Object.fromEntries(headers.entries()),
      signal: init.signal,
    }),
  )
}

export async function requestBlobWithHeaders(
  path: string,
  init: RequestInit = {},
): Promise<{ data: Blob; headers: Headers; status: number }> {
  const method = (init.method ?? "GET").toUpperCase()
  const headers = requestHeaders(init)

  try {
    return await getKehrblattAdminApi().raw.requestWithMeta<Blob>(method, path, {
      body: init.body ?? undefined,
      headers: Object.fromEntries(headers.entries()),
      signal: init.signal,
      responseType: "blob",
    })
  } catch (err) {
    if (err instanceof ControlClientError) {
      throw new ApiError(err.statusCode ?? err.status ?? 0, err.message, err.detail, {
        statusCode: err.statusCode ?? err.status,
        detail: err.detail,
        responseText: err.responseText,
        reasonCode: err.reasonCode,
        requestId: err.requestId,
        fieldErrors: err.fieldErrors,
      })
    }

    throw err
  }
}
